import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import {
  createWalletClient,
  defineChain,
  createPublicClient,
  custom,
  getAddress,
  type Address,
  type Abi,
  type EIP1193Provider,
  type Hash
} from "viem";

declare global {
  interface Window {
    ethereum?: EIP1193Provider;
  }
}

export interface WalletState {
  account: Address | null;
  chainId: number | null;
  available: boolean;
  busy: boolean;
  error: string | null;
  connect(): Promise<void>;
  switchChain(chainId: number): Promise<void>;
  signMessage(message: string): Promise<`0x${string}`>;
  writeContract(parameters: {
    address: Address;
    abi: Abi;
    functionName: string;
    args: readonly unknown[];
    value?: bigint;
    chainId: number;
  }): Promise<Hash>;
  waitForTransaction(hash: Hash, chainId: number): Promise<void>;
}

function provider(): EIP1193Provider | null {
  return window.ethereum ?? null;
}

export function useWallet(): WalletState {
  const [account, setAccount] = useState<Address | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ethereum = useMemo(provider, []);

  const refresh = useCallback(async () => {
    if (!ethereum) return;
    const [accounts, chain] = await Promise.all([
      ethereum.request({ method: "eth_accounts" }) as Promise<string[]>,
      ethereum.request({ method: "eth_chainId" }) as Promise<string>
    ]);
    setAccount(accounts[0] ? getAddress(accounts[0]) : null);
    setChainId(Number.parseInt(chain, 16));
  }, [ethereum]);

  useEffect(() => {
    void refresh();
    if (!ethereum?.on) return;
    const changed = () => void refresh();
    ethereum.on("accountsChanged", changed);
    ethereum.on("chainChanged", changed);
    return () => {
      ethereum.removeListener?.("accountsChanged", changed);
      ethereum.removeListener?.("chainChanged", changed);
    };
  }, [ethereum, refresh]);

  const connect = useCallback(async () => {
    if (!ethereum) {
      setError("No Ethereum wallet was found. Install or open a wallet extension first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ethereum.request({ method: "eth_requestAccounts" });
      await refresh();
    } catch (cause) {
      setError(walletError(cause));
    } finally {
      setBusy(false);
    }
  }, [ethereum, refresh]);

  const switchChain = useCallback(async (target: number) => {
    if (!ethereum) throw new Error("No Ethereum wallet is available");
    try {
      await ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${target.toString(16)}` }]
      });
      await refresh();
    } catch (cause) {
      throw new Error(walletError(cause));
    }
  }, [ethereum, refresh]);

  const signMessage = useCallback(async (message: string) => {
    if (!ethereum || !account) throw new Error("Connect a wallet first");
    const client = createWalletClient({ transport: custom(ethereum), account });
    return await client.signMessage({ account, message });
  }, [ethereum, account]);

  const writeContract = useCallback(async (
    parameters: {
      address: Address;
      abi: Abi;
      functionName: string;
      args: readonly unknown[];
      value?: bigint;
    chainId: number;
    }
  ) => {
    if (!ethereum || !account) throw new Error("Connect a wallet first");
    const active = await ethereum.request({method:"eth_accounts"}) as string[];
    if (active[0]?.toLowerCase() !== account.toLowerCase()) throw new Error("Wallet account changed. Reconnect and review the action again.");
    const chain = defineChain({id:parameters.chainId,name:parameters.chainId === 1 ? "Ethereum" : parameters.chainId === 11155111 ? "Sepolia" : "Local test",nativeCurrency:{name:parameters.chainId === 1 ? "Ether" : "Test Ether",symbol:"ETH",decimals:18},rpcUrls:{default:{http:[]}}});
    if (![1,11155111,31337].includes(chain.id)) throw new Error("Unsupported network");
    if (chain.id === 1 && !["setResolver", "setController", "safeTransferFrom"].includes(parameters.functionName)) {
      throw new Error("Qyoo acquisition is not currently open.");
    }
    const client = createWalletClient({ transport: custom(ethereum), account, chain });
    const {chainId: _chainId, ...transaction} = parameters;
    return await client.writeContract({
      ...transaction,
      account,
      chain
    } as never);
  }, [ethereum, account]);

  const waitForTransaction = useCallback(async (hash: Hash, expectedChain: number) => {
    if (!ethereum) throw new Error("No Ethereum wallet is available");
    const guarded = {request: async (request: Parameters<EIP1193Provider["request"]>[0]) => {
      const active = await ethereum.request({method:"eth_chainId"});
      if (Number.parseInt(String(active),16) !== expectedChain) throw new Error("Wallet network changed. Switch back to the selected network and refresh your Qyoo; do not blindly repeat a submitted transaction.");
      return ethereum.request(request);
    }};
    const client = createPublicClient({ transport: custom(guarded) });
    const receipt = await client.waitForTransactionReceipt({ hash, confirmations: 1, timeout: 90_000 });
    if (receipt.status !== "success") throw new Error("The transaction reverted on Ethereum");
  }, [ethereum]);

  return {
    account,
    chainId,
    available: ethereum !== null,
    busy,
    error,
    connect,
    switchChain,
    signMessage,
    writeContract,
    waitForTransaction
  };
}

function walletError(cause: unknown): string {
  if (cause instanceof Error && cause.message) return cause.message;
  return "The wallet did not complete the request.";
}

export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}
